import React, { useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BPeSeatStep from "./BPeSeatStep";
import BPePassengerStep from "./BPePassengerStep";
import BPeRouteStep from "./BPeRouteStep";
import BPePaymentStep from "./BPePaymentStep";
import BPeDoneStep from "./BPeDoneStep";
import BPeTripContextBar from "./BPeTripContextBar";
import BPeTripOptionList from "./BPeTripOptionList";
import BPeSuggestionBar from "./BPeSuggestionBar";
import BPeAssistedActions from "./BPeAssistedActions";
import BPeSendPreviewDialog from "./BPeSendPreviewDialog";
import { useBpeAssistedSend } from "./bpeAssistedSend";
import { renderSeatMapImage } from "./bpeSeatMapImage";
import { uploadOne } from "../../hooks/useS3Upload";
import {
  bpeWizardActionsSx,
  bpeWizardContentSx,
  bpeWizardPaperSx,
  bpeWizardStepBodyFillSx,
  bpeWizardStepBodySx,
  bpeWizardStepInnerSx,
  bpeWizardStepperSx,
  bpeWizardTitleSx,
  bpeGuideButtonSx,
  BPE_WIZARD_MAX_WIDTH,
} from "./bpeWizardLayout";
import {
  getBpeCidades,
  getBpeDraft,
  saveBpeDraft,
  clearBpeDraft,
  consultarViagens,
  getAssentos,
  preReserva,
  confirmarReserva,
  getBpeFormasPagamento,
  getBpeSuggestions,
  chargeBpePayment,
  emitirBpeAposPagamento,
  isImmediateBpePayment,
  needsOnlineBpeCharge,
  isRedeCardPayment,
  mapBpeAutoToResults,
  extractBpeEmissionFailure,
  cotarGratuidadeBpe,
} from "../../api/bpeApi";
import {
  validateRedeCard,
  cardToApiPayload,
  getMaxInstallmentsForPaymentMethod,
  clampInstallment,
} from "./BPeRedeCardForm";
import {
  clientRecordToPassenger,
  fetchClientRecord,
  fetchClientRelationships,
  mapRelationshipPeer,
  syncPassengersToClientRegistry,
  formatCpfDisplay,
} from "../../api/bpePassengerClients";
import { formatCityLabel, findCityByPostback, formatDataViagemForApi } from "./bpeCityDisplay";
import {
  normalizeTripClasse,
  pickTripByHistory,
  tripRequiresPassengerBirthDate,
  tripHasConexao,
  tripItineraryLabel,
} from "./bpeTripDisplay";
import { mapPassengerToApi } from "../../utils/bpePassengerApiMap";
import { completeBirthDateIso } from "../../utils/bpeBirthDateInput";

const STEP_KEYS = ["route", "trip", "seats", "passengers", "payment", "done"];
const DONE_STEP = 5;

function emptyPassenger(defaultCategory = "geral") {
  return {
    nome: "",
    documento: "",
    email: "",
    celular: "",
    clientId: null,
    clientSource: null,
    categoria: defaultCategory,
    declaracaoDoc: false,
    dataNascimento: "",
    rendaMensal: "",
  };
}

function passengerGratuityError(p, categories, t, requireBirthDate = false) {
  const meta = categories.find((c) => c.id === (p?.categoria || "geral"));
  if (meta?.requiresDeclaration && !p?.declaracaoDoc) {
    return t("bpeWizard.passengerBenefitDeclarationRequired");
  }
  if ((requireBirthDate || meta?.requiresBirthDate) && !completeBirthDateIso(p?.dataNascimento)) {
    return requireBirthDate && !meta?.requiresBirthDate
      ? t("bpeWizard.passengerBirthDateRequiredTrip")
      : t("bpeWizard.passengerBirthDateRequired");
  }
  if (
    meta?.requiresIncome &&
    (p?.rendaMensal == null || String(p.rendaMensal).trim() === "")
  ) {
    return t("bpeWizard.passengerIncomeRequired");
  }
  return null;
}

function isPassengerFormComplete(p, categories, requireBirthDate = false) {
  if (!p?.nome?.trim() || !p?.documento?.trim()) return false;
  return !passengerGratuityError(p, categories, () => "incomplete", requireBirthDate);
}

/** Cabeçalho de um trecho (ida/volta) nas etapas combinadas de horário e poltronas. */
function LegSectionHeader({ label, color = "default", route, detail }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ flexShrink: 0, mb: 0.25, minWidth: 0 }}
    >
      <Chip size="small" label={label} color={color} sx={{ fontWeight: 700, height: 22 }} />
      {route && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {route}
        </Typography>
      )}
      {detail && (
        <Typography variant="caption" fontWeight={700} color="primary.main" noWrap sx={{ flexShrink: 0 }}>
          {detail}
        </Typography>
      )}
    </Stack>
  );
}

/** Data de hoje no fuso local (evita UTC de `toISOString()` mudar o dia). */
function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateForInput(str) {
  if (!str) return "";
  const s = String(str).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  const [d, m, y] = s.split(/[/-]/);
  if (d && m && y) {
    const dd = d.padStart(2, "0");
    const mm = m.padStart(2, "0");
    const yyyy = y.length === 2 ? `20${y}` : y;
    return `${yyyy}-${mm}-${dd}`;
  }
  return s;
}

function formatDateDisplay(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = String(isoDate).substring(0, 10).split("-");
  if (y && m && d) return `${d}/${m}/${y}`;
  return isoDate;
}

function formatBRL(value) {
  if (value == null || value === "") return "—";
  const n = Number(String(value).replace(",", "."));
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/** Chave estável de uma viagem para re-casar a seleção após reconsultar (ordem pode variar). */
function tripKey(trip) {
  if (!trip) return "";
  return [trip.inicio, trip.fim, trip.classe, trip.valor, trip.servico]
    .map((x) => (x == null ? "" : String(x)))
    .join("|");
}

/** O rascunho tem conteúdo aproveitável? (evita "retomar" um estado vazio) */
function draftHasData(d) {
  if (!d || typeof d !== "object") return false;
  return Boolean(
    d.origem ||
      d.destino ||
      (Number(d.activeStep) || 0) > 0 ||
      (Array.isArray(d.passageiros) && d.passageiros.some((p) => p?.nome?.trim()))
  );
}

function parsePostback(postback) {
  if (typeof postback === "object" && postback?.id) return postback;
  if (typeof postback === "string" && postback.includes("##CIDADE##")) {
    const parts = postback.replace("##CIDADE##", "").split("#");
    return { id: parts[0], id_interno: parts[1], nome: parts[2], uf: parts[3], fonte: parts[4] };
  }
  return null;
}

function cityLabelFromPostback(postback, cities) {
  const found = findCityByPostback(cities, postback);
  if (found) return formatCityLabel(found);
  const parsed = parsePostback(postback);
  if (parsed?.nome) return formatCityLabel(parsed);
  return "";
}

function matchCityHint(hint, cities) {
  if (!hint || !Array.isArray(cities) || !cities.length) return "";
  const h = String(hint).trim();
  const found = cities.find(
    (c) =>
      String(c.postback) === h ||
      String(c.id) === h ||
      (c.postback && String(c.postback).includes(h)) ||
      (c.nome && h.toLowerCase().includes(String(c.nome).toLowerCase()))
  );
  return found ? String(found.postback || found.id) : "";
}

const BPeWizard = ({
  open,
  onClose,
  conversationId,
  onSuccess,
  initialOrigem = "",
  initialDestino = "",
  initialDataViagem = null,
  clientPrefill = null,
  channelInfo = null,
}) => {
  const { t } = useTranslation("chat");
  const assisted = useBpeAssistedSend({
    conversationId,
    channelId: channelInfo?.channelId,
    channelType: channelInfo?.channelType,
    projectId: channelInfo?.projectId ?? clientPrefill?.projectId,
    phone: channelInfo?.phone ?? clientPrefill?.celular,
  });
  const stepLabels = useMemo(
    () => STEP_KEYS.map((key) => t(`bpeWizard.steps.${key}`)),
    [t]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [tripNotice, setTripNotice] = useState(null);

  const [cidadesOrigem, setCidadesOrigem] = useState([]);
  const [cidadesDestino, setCidadesDestino] = useState([]);
  const [loadingCidadesOrigem, setLoadingCidadesOrigem] = useState(false);
  const [loadingCidadesDestino, setLoadingCidadesDestino] = useState(false);
  const swapDestinoTargetRef = useRef(null);
  const [origem, setOrigem] = useState("");
  const [destino, setDestino] = useState("");
  const [dataViagem, setDataViagem] = useState(() => todayLocalISO());
  const [quantidadePassageiros, setQuantidadePassageiros] = useState(1);
  // Ida e volta: as duas pernas são coletadas lado a lado (horário + poltronas),
  // pré-reservadas e cobradas UMA única vez ao final.
  const [tripType, setTripType] = useState("one_way");
  const [dataVolta, setDataVolta] = useState("");
  const [legPhase] = useState("ida"); // mantido para compatibilidade (sempre "ida")

  const [viagens, setViagens] = useState([]);
  const [opcaoViagem, setOpcaoViagem] = useState("");
  const [assentos, setAssentos] = useState({ disponiveis: [], grid: [], selected: {} });
  const [activeSeatPassenger, setActiveSeatPassenger] = useState(0);
  // Ida e volta combinadas: a perna de volta tem sua própria lista de horários,
  // mapa de assentos e seleção, coletadas lado a lado (sem fluxo sequencial).
  const [viagensVolta, setViagensVolta] = useState([]);
  const [opcaoVolta, setOpcaoVolta] = useState("");
  const [assentosVolta, setAssentosVolta] = useState({ disponiveis: [], grid: [], selected: {} });
  const [activeSeatPassengerVolta, setActiveSeatPassengerVolta] = useState(0);
  const assentosVoltaTripRef = useRef(null);
  const [assentosConexao, setAssentosConexao] = useState({ disponiveis: [], grid: [], selected: {} });
  const [activeSeatPassengerConexao, setActiveSeatPassengerConexao] = useState(0);
  const [assentosConexaoVolta, setAssentosConexaoVolta] = useState({ disponiveis: [], grid: [], selected: {} });
  const [activeSeatPassengerConexaoVolta, setActiveSeatPassengerConexaoVolta] = useState(0);
  const assentosConexaoTripRef = useRef(null);
  const assentosConexaoVoltaTripRef = useRef(null);
  const [activePassengerStep, setActivePassengerStep] = useState(0);
  const [passengerValidationFlash, setPassengerValidationFlash] = useState(false);
  const [passageiros, setPassageiros] = useState([emptyPassenger()]);
  const [passengerCategories, setPassengerCategories] = useState([]);
  const [passengerCategoriesVolta, setPassengerCategoriesVolta] = useState([]);
  const [gratuityFareTotal, setGratuityFareTotal] = useState(null);
  const [requestClient, setRequestClient] = useState(null);
  const [relatedClients, setRelatedClients] = useState([]);
  const [syncPassengerRegistry, setSyncPassengerRegistry] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [paymentCharge, setPaymentCharge] = useState(null);
  const [paymentPending, setPaymentPending] = useState(false);
  const [redeCard, setRedeCard] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
    installments: "1",
  });
  const [preReservaExpiresAt, setPreReservaExpiresAt] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  // Pré-visualização do envio ao cliente (texto/imagem) antes de jogar no compositor.
  const [previewAction, setPreviewAction] = useState(null);
  const emitLockRef = useRef(false);
  const [salesSuggestions, setSalesSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // Sugestões inferidas (histórico/base) só são aplicadas quando o atendente opta
  // por "Aplicar tudo" ou "Usar sugestão". Trechos vindos das tags continuam diretos.
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(false);
  // Persistência/retomada da venda (estado salvo na solicitação) + navegação livre nas etapas.
  const [draftPhase, setDraftPhase] = useState("idle"); // idle | loading | fresh | restored
  const [resumeAvailable, setResumeAvailable] = useState(false);
  const [resumeCrossChannel, setResumeCrossChannel] = useState(false);
  // Retomada degradada: alguma consulta viva (horários/assentos) falhou ao reidratar.
  // Em vez de rebaixar a etapa silenciosamente, avisamos e oferecemos "Recarregar".
  const [resumeDegraded, setResumeDegraded] = useState(false);
  const [resumeReloadKey, setResumeReloadKey] = useState(0);
  const [downstreamStale, setDownstreamStale] = useState(false);
  const [furthestStep, setFurthestStep] = useState(0);
  const resumeShownStepRef = useRef(null);
  const draftReadyRef = useRef(false);
  const restoringRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const assentosTripRef = useRef(null);
  const routeTouchedRef = useRef(false);
  const suggestionsAppliedRef = useRef({
    route: false,
    trip: false,
    passengers: false,
    payment: false,
  });

  React.useEffect(() => {
    if (!open || !conversationId) return undefined;
    let cancelled = false;
    setSuggestionsLoading(true);
    getBpeSuggestions(conversationId)
      .then((data) => {
        if (!cancelled) setSalesSuggestions(data);
      })
      .catch(() => {
        if (!cancelled) setSalesSuggestions(null);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, conversationId]);

  const applyRouteSuggestion = useCallback(
    (suggestions) => {
      const route = suggestions?.route;
      if (!route) return;
      if (route.origemPostback) setOrigem(route.origemPostback);
      // O destino é fixado após a lista de destinos carregar (effect dedicado).
      if (route.destinoPostback) setDestino(route.destinoPostback);
      if (suggestions.travelDate) {
        const f = formatDateForInput(suggestions.travelDate);
        if (f) setDataViagem(f);
      }
      if (suggestions.passengerCount > 1) {
        setQuantidadePassageiros(Math.min(5, Math.max(1, Number(suggestions.passengerCount))));
      }
      suggestionsAppliedRef.current.route = true;
      suggestionsAppliedRef.current.routeDestino = false;
    },
    []
  );

  const applyTripSuggestion = useCallback(() => {
    if (!viagens.length) return;
    const rec = pickTripByHistory(viagens, salesSuggestions, quantidadePassageiros);
    if (rec) setOpcaoViagem(String(rec.apiIndex + 1));
    suggestionsAppliedRef.current.trip = true;
  }, [viagens, salesSuggestions, quantidadePassageiros]);

  const applyPassengerSuggestion = useCallback(() => {
    const p = salesSuggestions?.passenger;
    if (!p?.nome) return;
    setPassageiros((prev) => {
      const next = [...prev];
      next[0] = {
        ...next[0],
        nome: p.nome,
        documento: formatCpfDisplay(p.documento) || next[0].documento,
        email: p.email || "",
        celular: p.celular || "",
        clientId: p.clientId || null,
        clientSource: p.clientId ? "inferred" : null,
      };
      return next;
    });
    suggestionsAppliedRef.current.passengers = true;
  }, [salesSuggestions]);

  const applyPaymentSuggestion = useCallback(() => {
    const id = salesSuggestions?.paymentMethodId;
    if (!id || !paymentMethods.length) return;
    const found = paymentMethods.find((m) => String(m.id) === String(id));
    if (found) setSelectedPaymentId(String(found.id));
    suggestionsAppliedRef.current.payment = true;
  }, [salesSuggestions, paymentMethods]);

  const handleApplySuggestion = useCallback(() => {
    if (!salesSuggestions) return;
    if (activeStep === 0) {
      // "Aplicar tudo": habilita o preenchimento automático das próximas etapas
      // (passageiro/pagamento) e aplica o trecho/data/qtde agora.
      setAutoApplyEnabled(true);
      applyRouteSuggestion(salesSuggestions);
    }
    if (activeStep === 1) applyTripSuggestion();
    if (activeStep === 3) applyPassengerSuggestion();
    if (activeStep === 4) applyPaymentSuggestion();
  }, [
    activeStep,
    salesSuggestions,
    applyRouteSuggestion,
    applyTripSuggestion,
    applyPassengerSuggestion,
    applyPaymentSuggestion,
  ]);

  React.useEffect(() => {
    if (!open) {
      routeTouchedRef.current = false;
      setAutoApplyEnabled(false);
      suggestionsAppliedRef.current = {
        route: false,
        routeDestino: false,
        trip: false,
        passengers: false,
        payment: false,
      };
    }
  }, [open]);

  const selectedPayment = useMemo(
    () => paymentMethods.find((m) => String(m.id) === String(selectedPaymentId)),
    [paymentMethods, selectedPaymentId]
  );

  // O aviso de "Venda retomada" recolhe sozinho assim que o atendente navega de etapa
  // (já cumpriu seu papel de sinalizar a retomada), liberando espaço nas demais telas.
  React.useEffect(() => {
    if (!resumeAvailable) {
      resumeShownStepRef.current = null;
      return;
    }
    if (resumeShownStepRef.current == null) {
      resumeShownStepRef.current = activeStep;
    } else if (activeStep !== resumeShownStepRef.current) {
      setResumeAvailable(false);
    }
  }, [resumeAvailable, activeStep]);

  const loadCidadesOrigem = useCallback(async () => {
    setLoadingCidadesOrigem(true);
    try {
      const list = await getBpeCidades("origem");
      setCidadesOrigem(Array.isArray(list) ? list : []);
    } catch (e) {
      setCidadesOrigem([]);
      setError(e.message || t("bpeWizard.errors.loadCities"));
    } finally {
      setLoadingCidadesOrigem(false);
    }
  }, [t]);

  const loadCidadesDestino = useCallback(async (origemId) => {
    setLoadingCidadesDestino(true);
    setCidadesDestino([]);
    try {
      const list = await getBpeCidades("destino", origemId);
      setCidadesDestino(Array.isArray(list) ? list : []);
    } catch (e) {
      setCidadesDestino([]);
      setError(e.message || t("bpeWizard.errors.loadCities"));
    } finally {
      setLoadingCidadesDestino(false);
    }
  }, [t]);

  React.useEffect(() => {
    if (open && conversationId) loadCidadesOrigem();
  }, [open, conversationId, loadCidadesOrigem]);

  /** Aplica defaults de uma venda nova (sem rascunho). */
  const applyFreshDefaults = useCallback(() => {
    setOrigem("");
    setDestino("");
    setQuantidadePassageiros(1);
    setTripType("one_way");
    setDataVolta("");
    setViagens([]);
    setOpcaoViagem("");
    setAssentos({ disponiveis: [], grid: [], selected: {} });
    setAssentosConexao({ disponiveis: [], grid: [], selected: {} });
    setViagensVolta([]);
    setOpcaoVolta("");
    setAssentosVolta({ disponiveis: [], grid: [], selected: {} });
    setAssentosConexaoVolta({ disponiveis: [], grid: [], selected: {} });
    setActiveSeatPassengerVolta(0);
    assentosVoltaTripRef.current = null;
    assentosConexaoTripRef.current = null;
    assentosConexaoVoltaTripRef.current = null;
    setPassageiros([emptyPassenger()]);
    setSelectedPaymentId("");
    setPreReservaExpiresAt(null);
    setActiveStep(0);
    setFurthestStep(0);
    assentosTripRef.current = null;
    const today = todayLocalISO();
    const f = initialDataViagem ? formatDateForInput(initialDataViagem) : "";
    setDataViagem(f || today);
  }, [initialDataViagem]);

  /** Reidrata o wizard a partir do rascunho salvo (refaz consultas vivas: viagens/assentos). */
  const restoreFromDraft = useCallback(
    async (d, isCancelled) => {
      if (d.origem) setOrigem(d.origem);
      if (d.destino) setDestino(d.destino);
      if (d.dataViagem) setDataViagem(formatDateForInput(d.dataViagem));
      if (d.quantidadePassageiros) setQuantidadePassageiros(Number(d.quantidadePassageiros) || 1);
      const restoringRoundTrip = d.tripType === "round_trip";
      if (d.tripType) setTripType(d.tripType);
      if (d.dataVolta) setDataVolta(d.dataVolta);
      if (Array.isArray(d.passageiros) && d.passageiros.length) {
        setPassageiros(d.passageiros.map((p) => ({ ...emptyPassenger(), ...p })));
      }
      if (d.selectedPaymentId) setSelectedPaymentId(String(d.selectedPaymentId));
      if (d.preReservaExpiresAt) setPreReservaExpiresAt(d.preReservaExpiresAt);

      // Nunca retomamos na tela final nem em pagamento pendente: cai no máximo no passo de pagamento.
      let target = Math.min(Number(d.activeStep) || 0, 4);
      // Otimismo: posiciona o stepper/título no passo salvo já durante o carregamento,
      // evitando exibir "Trecho" enquanto a reidratação acontece.
      setActiveStep(target);
      setFurthestStep(target);
      // Sinaliza degradação se uma consulta viva falhar (em vez de rebaixar em silêncio).
      let degraded = false;

      if (target >= 1 && d.origem && d.destino && d.dataViagem) {
        try {
          const result = await consultarViagens(conversationId, {
            origem: d.origem,
            destino: d.destino,
            data_viagem: formatDataViagemForApi(formatDateForInput(d.dataViagem)),
            quantidade_passageiros: Number(d.quantidadePassageiros) || 1,
            // IMPORTANTE: não limpar o estado de servidor (legs/pré-reservas) ao reidratar.
            reset_session: false,
          });
          if (isCancelled && isCancelled()) return;
          const list = Array.isArray(result?.viagens) ? result.viagens : [];
          setViagens(list);
          let opt = "";
          if (list.length) {
            if (d.selectedTripKey) {
              const idx = list.findIndex((v) => tripKey(v) === d.selectedTripKey);
              if (idx >= 0) opt = String(idx + 1);
            }
            if (!opt && d.opcaoViagem && Number(d.opcaoViagem) <= list.length) {
              opt = String(d.opcaoViagem);
            }
          }
          setOpcaoViagem(opt);
          if (!opt) {
            target = 1; // não deu para recuperar a viagem: volta para a escolha do horário.
            degraded = true;
          }

          if (target >= 2 && opt) {
            try {
              const seatRes = await getAssentos(conversationId, Number(opt));
              if (isCancelled && isCancelled()) return;
              const selected =
                d.seats && typeof d.seats === "object" && !Array.isArray(d.seats) ? d.seats : {};
              setAssentos({
                disponiveis: seatRes.assentosdisponiveis || [],
                grid: seatRes.assentos || [],
                selected,
              });
              if (Array.isArray(seatRes.passengerCategories) && seatRes.passengerCategories.length) {
                setPassengerCategories(seatRes.passengerCategories);
              }
              assentosTripRef.current = opt;
              if (seatRes.hasConexoes) {
                try {
                  const cxRes = await getAssentos(conversationId, Number(opt), { perna: 1 });
                  if (isCancelled && isCancelled()) return;
                  const cxSelected =
                    d.seatsConexao && typeof d.seatsConexao === "object" && !Array.isArray(d.seatsConexao)
                      ? d.seatsConexao
                      : {};
                  setAssentosConexao({
                    disponiveis: cxRes.assentosdisponiveis || [],
                    grid: cxRes.assentos || [],
                    selected: cxSelected,
                  });
                  assentosConexaoTripRef.current = opt;
                } catch (_) {
                  /* conexão opcional na retomada */
                }
              }
            } catch (_) {
              target = Math.min(target, 1);
              degraded = true;
            }
          }
        } catch (_) {
          target = 0;
          degraded = true;
        }
      }

      // Volta (ida e volta combinadas): reidrata lista e assentos do trecho invertido.
      if (restoringRoundTrip && target >= 1 && d.origem && d.destino && d.dataVolta) {
        try {
          const vResult = await consultarViagens(conversationId, {
            origem: d.destino,
            destino: d.origem,
            data_viagem: formatDataViagemForApi(formatDateForInput(d.dataVolta)),
            quantidade_passageiros: Number(d.quantidadePassageiros) || 1,
            reset_session: false,
          });
          if (isCancelled && isCancelled()) return;
          const vlist = Array.isArray(vResult?.viagens) ? vResult.viagens : [];
          setViagensVolta(vlist);
          let vopt = "";
          if (vlist.length) {
            if (d.selectedTripKeyVolta) {
              const idx = vlist.findIndex((v) => tripKey(v) === d.selectedTripKeyVolta);
              if (idx >= 0) vopt = String(idx + 1);
            }
            if (!vopt && d.opcaoVolta && Number(d.opcaoVolta) <= vlist.length) {
              vopt = String(d.opcaoVolta);
            }
          }
          setOpcaoVolta(vopt);
          if (!vopt) {
            target = Math.min(target, 1);
            degraded = true;
          }

          if (target >= 2 && vopt) {
            try {
              const vseat = await getAssentos(conversationId, Number(vopt));
              if (isCancelled && isCancelled()) return;
              const vselected =
                d.seatsVolta && typeof d.seatsVolta === "object" && !Array.isArray(d.seatsVolta)
                  ? d.seatsVolta
                  : {};
              setAssentosVolta({
                disponiveis: vseat.assentosdisponiveis || [],
                grid: vseat.assentos || [],
                selected: vselected,
              });
              if (Array.isArray(vseat.passengerCategories) && vseat.passengerCategories.length) {
                setPassengerCategoriesVolta(vseat.passengerCategories);
              }
              assentosVoltaTripRef.current = vopt;
              if (vseat.hasConexoes) {
                try {
                  const vcx = await getAssentos(conversationId, Number(vopt), { perna: 1 });
                  if (isCancelled && isCancelled()) return;
                  const vcxSelected =
                    d.seatsConexaoVolta &&
                    typeof d.seatsConexaoVolta === "object" &&
                    !Array.isArray(d.seatsConexaoVolta)
                      ? d.seatsConexaoVolta
                      : {};
                  setAssentosConexaoVolta({
                    disponiveis: vcx.assentosdisponiveis || [],
                    grid: vcx.assentos || [],
                    selected: vcxSelected,
                  });
                  assentosConexaoVoltaTripRef.current = vopt;
                } catch (_) {
                  /* conexão da volta opcional na retomada */
                }
              }
            } catch (_) {
              target = Math.min(target, 1);
              degraded = true;
            }
          }
        } catch (_) {
          target = Math.min(target, 1);
          degraded = true;
        }
      }

      // Reabre direto numa cobrança pendente (Pix/link já gerado), preservando o link.
      if (target === 4 && d.paymentPending && d.paymentCharge) {
        setPaymentCharge(d.paymentCharge);
        setPaymentPending(true);
      }

      setActiveStep(target);
      setFurthestStep(target);
      setResumeDegraded(degraded);
    },
    [conversationId]
  );

  // Coordenador de abertura: carrega o rascunho da solicitação e retoma, ou inicia venda nova.
  React.useEffect(() => {
    if (!open || !conversationId) return undefined;
    let cancelled = false;
    draftReadyRef.current = false;
    restoringRef.current = true;
    setDraftPhase("loading");
    setResumeAvailable(false);
    setResumeCrossChannel(false);
    setResumeDegraded(false);
    setDownstreamStale(false);
    setError(null);
    setWarning(null);
    setTripNotice(null);
    setPaymentPending(false);
    setPaymentCharge(null);
    setResultado(null);
    setDeliveryInfo(null);
    emitLockRef.current = false;
    applyFreshDefaults();

    (async () => {
      let draft = null;
      try {
        const draftRes = await getBpeDraft(conversationId, {
          phone: clientPrefill?.celular,
          cpf: clientPrefill?.documento,
          clientId: clientPrefill?.clientId,
        });
        draft = draftRes?.draft ?? draftRes;
      } catch (_) {
        draft = null;
      }
      if (cancelled) return;
      if (draftHasData(draft)) {
        try {
          await restoreFromDraft(draft, () => cancelled);
        } catch (_) {
          /* fallback para venda nova */
        }
        if (cancelled) return;
        setResumeAvailable(true);
        setResumeCrossChannel(draft.source === "context");
        setDraftPhase("restored");
      } else {
        setDraftPhase("fresh");
      }
      restoringRef.current = false;
      draftReadyRef.current = true;
    })();

    return () => {
      cancelled = true;
      restoringRef.current = false;
    };
  }, [open, conversationId, initialDataViagem, applyFreshDefaults, restoreFromDraft, resumeReloadKey]);

  React.useEffect(() => {
    if (draftPhase !== "fresh" || legPhase !== "ida" || !cidadesOrigem.length) return;
    if (initialOrigem) {
      const matched = matchCityHint(initialOrigem, cidadesOrigem);
      if (matched) setOrigem(matched);
    }
  }, [draftPhase, legPhase, initialOrigem, cidadesOrigem]);

  React.useEffect(() => {
    if (draftPhase !== "fresh" || legPhase !== "ida" || !cidadesDestino.length) return;
    if (initialDestino) {
      const matched = matchCityHint(initialDestino, cidadesDestino);
      if (matched) setDestino(matched);
    }
  }, [draftPhase, legPhase, initialDestino, cidadesDestino]);

  // Após "Aplicar tudo", quando a lista de destinos terminar de carregar, fixa o destino sugerido.
  // Só na ida — na volta o destino vem da inversão do trecho (swap), não da sugestão.
  React.useEffect(() => {
    if (!open || !autoApplyEnabled || legPhase !== "ida" || !salesSuggestions) return;
    if (suggestionsAppliedRef.current.routeDestino || routeTouchedRef.current) return;
    const route = salesSuggestions.route;
    if (!route?.destinoPostback || !cidadesDestino.length || !origem) return;
    setDestino(route.destinoPostback);
    suggestionsAppliedRef.current.routeDestino = true;
  }, [open, autoApplyEnabled, legPhase, salesSuggestions, cidadesDestino, origem]);

  React.useEffect(() => {
    const o = parsePostback(origem);
    if (o?.id) loadCidadesDestino(Number(o.id));
    else {
      setCidadesDestino([]);
      setLoadingCidadesDestino(false);
    }
  }, [origem, loadCidadesDestino]);

  React.useEffect(() => {
    if (!swapDestinoTargetRef.current || loadingCidadesDestino || !cidadesDestino.length) return;
    const target = swapDestinoTargetRef.current;
    swapDestinoTargetRef.current = null;
    const found = findCityByPostback(cidadesDestino, target);
    if (found) setDestino(String(found.postback || found.id));
  }, [cidadesDestino, loadingCidadesDestino]);

  const handleSwapRoute = () => {
    if (!origem || !destino) return;
    routeTouchedRef.current = true;
    setError(null);
    invalidateDownstream(0);
    const prevDestino = destino;
    swapDestinoTargetRef.current = origem;
    setOrigem(prevDestino);
    setDestino("");
  };

  const origemSel = useMemo(() => findCityByPostback(cidadesOrigem, origem), [cidadesOrigem, origem]);
  const destinoSel = useMemo(() => findCityByPostback(cidadesDestino, destino), [cidadesDestino, destino]);

  const isRoundTrip = tripType === "round_trip";

  const effectivePassengerCategories = useMemo(() => {
    if (!isRoundTrip || !passengerCategoriesVolta.length) return passengerCategories;
    const byId = new Map();
    [...passengerCategories, ...passengerCategoriesVolta].forEach((c) => {
      if (c?.id) byId.set(c.id, c);
    });
    return [...byId.values()];
  }, [passengerCategories, passengerCategoriesVolta, isRoundTrip]);

  const routeSummary = useMemo(() => {
    const origemNome = cityLabelFromPostback(origem, cidadesOrigem);
    const destinoNome = cityLabelFromPostback(destino, cidadesDestino);
    if (!origemNome && !destinoNome) return null;
    return t("bpeWizard.routeSummary", {
      origem: origemNome || "—",
      destino: destinoNome || "—",
      date: formatDateDisplay(formatDateForInput(dataViagem)),
      count: quantidadePassageiros,
    });
  }, [origem, destino, dataViagem, quantidadePassageiros, cidadesOrigem, cidadesDestino, t]);

  const selectedTrip = useMemo(() => {
    const idx = Number(opcaoViagem) - 1;
    if (!opcaoViagem || idx < 0 || idx >= viagens.length) return null;
    return viagens[idx];
  }, [opcaoViagem, viagens]);

  const selectedTripVolta = useMemo(() => {
    const idx = Number(opcaoVolta) - 1;
    if (!opcaoVolta || idx < 0 || idx >= viagensVolta.length) return null;
    return viagensVolta[idx];
  }, [opcaoVolta, viagensVolta]);

  const hasConexaoIda = tripHasConexao(selectedTrip) || assentosConexao.grid.length > 0;
  const hasConexaoVolta = tripHasConexao(selectedTripVolta) || assentosConexaoVolta.grid.length > 0;

  // Resumo do trecho de volta = trecho invertido (origem↔destino) na data da volta.
  const voltaRouteSummary = useMemo(() => {
    const origemNome = cityLabelFromPostback(origem, cidadesOrigem);
    const destinoNome = cityLabelFromPostback(destino, cidadesDestino);
    if (!origemNome && !destinoNome) return null;
    return t("bpeWizard.routeSummary", {
      origem: destinoNome || "—",
      destino: origemNome || "—",
      date: formatDateDisplay(formatDateForInput(dataVolta)),
      count: quantidadePassageiros,
    });
  }, [origem, destino, dataVolta, quantidadePassageiros, cidadesOrigem, cidadesDestino, t]);

  const handleBack = () => {
    setError(null);
    if (activeStep === 4 && paymentPending) {
      setPaymentPending(false);
      setPaymentCharge(null);
      emitLockRef.current = false;
      return;
    }
    if (activeStep > 0) setActiveStep((s) => s - 1);
  };

  /** Avança e registra o passo mais distante alcançado (habilita navegação livre até ele). */
  const goForward = useCallback((step) => {
    setActiveStep(step);
    setFurthestStep((f) => Math.max(f, step));
  }, []);

  const tripBirthRequired = useMemo(
    () =>
      tripRequiresPassengerBirthDate(selectedTrip) ||
      (isRoundTrip && tripRequiresPassengerBirthDate(selectedTripVolta)),
    [selectedTrip, selectedTripVolta, isRoundTrip]
  );

  /** Uma etapa tem dados válidos? (define o que pode ser navegado e o que precisa de revisão) */
  const isStepComplete = useCallback(
    (i) => {
      switch (i) {
        case 0:
          return Boolean(origem && destino && dataViagem);
        case 1: {
          const idaOk = Boolean(opcaoViagem) && Number(opcaoViagem) - 1 < viagens.length;
          if (!isRoundTrip) return idaOk;
          const voltaOk = Boolean(opcaoVolta) && Number(opcaoVolta) - 1 < viagensVolta.length;
          return idaOk && voltaOk;
        }
        case 2: {
          const qtd = Math.max(1, Number(quantidadePassageiros) || 1);
          const legDone = (sel, grid) => {
            for (let k = 0; k < qtd; k++) {
              if (!sel[k]) return false;
            }
            return grid.length > 0;
          };
          if (!legDone(assentos.selected, assentos.grid)) return false;
          if (isRoundTrip && !legDone(assentosVolta.selected, assentosVolta.grid)) return false;
          return true;
        }
        case 3:
          return (
            passageiros.length > 0 &&
            passageiros.every((p) => isPassengerFormComplete(p, effectivePassengerCategories, tripBirthRequired))
          );
        case 4:
          return Boolean(selectedPaymentId);
        default:
          return false;
      }
    },
    [origem, destino, dataViagem, opcaoViagem, viagens.length, quantidadePassageiros, assentos.selected, assentos.grid.length, passageiros, effectivePassengerCategories, selectedPaymentId, isRoundTrip, opcaoVolta, viagensVolta.length, assentosVolta.selected, assentosVolta.grid.length, tripBirthRequired]
  );

  /** Pode navegar diretamente para o passo `i` pelo cabeçalho? */
  const canNavigateTo = useCallback(
    (i) => {
      if (i === activeStep || i === DONE_STEP) return false;
      if (activeStep === DONE_STEP || paymentPending || loading) return false;
      if (draftPhase === "loading") return false;
      if (i < activeStep) return true; // voltar é sempre permitido
      if (i > furthestStep) return false; // não pula adiante de onde já chegou
      for (let j = 0; j < i; j++) {
        if (!isStepComplete(j)) return false; // etapas anteriores precisam estar completas
      }
      return true;
    },
    [activeStep, paymentPending, loading, draftPhase, furthestStep, isStepComplete]
  );

  const goToStep = useCallback(
    (i) => {
      if (!canNavigateTo(i)) return;
      setError(null);
      setActiveStep(i);
    },
    [canNavigateTo]
  );

  /**
   * Invalida as seleções das etapas seguintes quando uma etapa anterior muda,
   * forçando revisão (ex.: trocar o trecho exige reconsultar horários/assentos).
   */
  const invalidateDownstream = useCallback(
    (fromStep) => {
      if (furthestStep <= fromStep) return; // ainda não havia avançado: nada a invalidar
      if (fromStep <= 0) {
        setViagens([]);
        setOpcaoViagem("");
        setViagensVolta([]);
        setOpcaoVolta("");
        assentosTripRef.current = null;
        assentosVoltaTripRef.current = null;
      }
      if (fromStep <= 1) {
        setAssentos({ disponiveis: [], grid: [], selected: {} });
        setAssentosVolta({ disponiveis: [], grid: [], selected: {} });
        setAssentosConexao({ disponiveis: [], grid: [], selected: {} });
        setAssentosConexaoVolta({ disponiveis: [], grid: [], selected: {} });
        assentosTripRef.current = null;
        assentosVoltaTripRef.current = null;
        assentosConexaoTripRef.current = null;
        assentosConexaoVoltaTripRef.current = null;
      }
      setFurthestStep(fromStep);
      setDownstreamStale(true);
    },
    [furthestStep]
  );

  /** Snapshot serializável do wizard (sem dados de cartão, por segurança). */
  const buildDraft = useCallback(
    () => ({
      version: 1,
      activeStep: activeStep === DONE_STEP ? 4 : activeStep,
      tripType,
      dataVolta,
      origem,
      destino,
      dataViagem,
      quantidadePassageiros,
      opcaoViagem,
      selectedTripKey: tripKey(selectedTrip),
      seats: assentos.selected,
      seatsConexao: assentosConexao.selected,
      // Trecho de volta (ida e volta combinadas).
      opcaoVolta,
      selectedTripKeyVolta: tripKey(selectedTripVolta),
      seatsVolta: assentosVolta.selected,
      seatsConexaoVolta: assentosConexaoVolta.selected,
      passageiros: passageiros.map((p) => ({
        nome: p.nome || "",
        documento: p.documento || "",
        email: p.email || "",
        celular: p.celular || "",
        clientId: p.clientId ?? null,
        clientSource: p.clientSource ?? null,
        categoria: p.categoria || "geral",
        declaracaoDoc: !!p.declaracaoDoc,
        dataNascimento: p.dataNascimento || "",
        rendaMensal: p.rendaMensal ?? "",
      })),
      selectedPaymentId,
      preReservaExpiresAt,
      // Cobrança em andamento (Pix/link já gerado): permite reabrir direto no pagamento
      // pendente, sem perder o link nem regenerar a cobrança. Não guarda dados de cartão.
      paymentPending,
      paymentCharge: paymentPending ? paymentCharge : null,
    }),
    [
      activeStep,
      tripType,
      dataVolta,
      origem,
      destino,
      dataViagem,
      quantidadePassageiros,
      opcaoViagem,
      selectedTrip,
      assentos.selected,
      assentosConexao.selected,
      opcaoVolta,
      selectedTripVolta,
      assentosVolta.selected,
      assentosConexaoVolta.selected,
      passageiros,
      selectedPaymentId,
      preReservaExpiresAt,
      paymentPending,
      paymentCharge,
    ]
  );

  // Autosave (debounced) das escolhas na solicitação, para retomar a venda depois.
  React.useEffect(() => {
    if (!open || !conversationId) return undefined;
    if (restoringRef.current || !draftReadyRef.current) return undefined;
    if (draftPhase === "loading") return undefined;
    if (activeStep === DONE_STEP) return undefined;
    if (!(origem || destino || activeStep > 0)) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveBpeDraft(conversationId, buildDraft()).catch(() => {});
    }, 700);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [open, conversationId, draftPhase, activeStep, origem, destino, buildDraft]);

  const handleConsultar = async () => {
    setError(null);
    setTripNotice(null);
    setDownstreamStale(false);
    setLoading(true);
    try {
      if (!origem || !destino) {
        setError(t("bpeWizard.errors.originDestination"));
        return;
      }
      const dataStr = formatDataViagemForApi(dataViagem);
      if (!dataStr) {
        setError(t("bpeWizard.errors.travelDate"));
        return;
      }
      const payload = {
        origem,
        destino,
        data_viagem: dataStr,
        quantidade_passageiros: quantidadePassageiros,
        // Perna de ida = início de uma nova venda: limpa trechos/passageiros remanescentes no backend.
        reset_session: legPhase === "ida",
      };
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.info("[bpe-wizard] consultar-viagens request", payload);
      }
      const result = await consultarViagens(conversationId, payload);
      const list = Array.isArray(result?.viagens) ? result.viagens : [];
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.info("[bpe-wizard] consultar-viagens response", {
          count: list.length,
          _debug: result?._debug,
        });
      }
      setViagens(list);
      assentosTripRef.current = null;
      if (list.length === 0) {
        const aviso = result?.aviso;
        let notice =
          aviso?.codigo === "sem_corridas_trecho_valido"
            ? t("bpeWizard.notices.noTripsRouteValid", {
                date: formatDateDisplay(formatDateForInput(dataViagem)),
                origem: aviso.origemLabel || "—",
                destino: aviso.destinoLabel || "—",
              })
            : aviso?.mensagem ||
              t("bpeWizard.notices.noTrips", {
                date: formatDateDisplay(formatDateForInput(dataViagem)),
              });
        if (process.env.NODE_ENV === "development" && result?._debug?.stages?.length) {
          const last = result._debug.stages[result._debug.stages.length - 1];
          if (last?.totalbus?.mensagem && !aviso?.mensagem) {
            notice += ` (${last.totalbus.mensagem})`;
          } else if (
            aviso?.codigo === "ids_cidade_invalidos" ||
            result._debug.stages.find((s) => s.stage === "totalbus_invalid_ids")
          ) {
            notice += " (IDs de cidade inválidos para o parceiro — veja o console/terminal da API)";
          } else if (aviso?.origemParceiroId) {
            notice += ` (Totalbus: ${aviso.origemParceiroId} → ${aviso.destinoParceiroId})`;
          }
        }
        setTripNotice(notice);
        return;
      }

      if (isRoundTrip) {
        // Busca também os horários da volta (trecho invertido, na data da volta).
        const voltaDataStr = formatDataViagemForApi(dataVolta);
        const voltaResult = await consultarViagens(conversationId, {
          origem: destino,
          destino: origem,
          data_viagem: voltaDataStr,
          quantidade_passageiros: quantidadePassageiros,
          reset_session: false,
        });
        const voltaList = Array.isArray(voltaResult?.viagens) ? voltaResult.viagens : [];
        setViagensVolta(voltaList);
        assentosVoltaTripRef.current = null;
        if (voltaList.length === 0) {
          setTripNotice(
            t("bpeWizard.notices.noTripsReturn", {
              date: formatDateDisplay(formatDateForInput(dataVolta)),
            })
          );
          return; // sem volta não há como vender ida e volta combinadas
        }
        const recVolta = pickTripByHistory(voltaList, null, quantidadePassageiros);
        setOpcaoVolta(recVolta ? String(recVolta.apiIndex + 1) : "");
      }

      goForward(1);
      const rec = pickTripByHistory(
        list,
        autoApplyEnabled ? salesSuggestions : null,
        quantidadePassageiros
      );
      setOpcaoViagem(rec ? String(rec.apiIndex + 1) : "");
      if (rec) suggestionsAppliedRef.current.trip = true;
    } catch (e) {
      setError(e.message || t("bpeWizard.errors.consult"));
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarViagem = async () => {
    if (!opcaoViagem || (isRoundTrip && !opcaoVolta)) {
      setError(t(isRoundTrip ? "bpeWizard.errors.selectBothTrips" : "bpeWizard.errors.selectTrip"));
      return;
    }
    setError(null);
    setDownstreamStale(false);
    setActiveSeatPassenger(0);
    setActiveSeatPassengerVolta(0);

    if (!isRoundTrip) {
      goForward(2);
      // Recarrega o mapa se a viagem mudou desde o último carregamento.
      if (assentosTripRef.current !== opcaoViagem) {
        setAssentos({ disponiveis: [], grid: [], selected: {} });
      }
      return;
    }

    // Ida e volta: carrega os DOIS mapas de assentos. Re-consulta cada perna no backend
    // para fixar o trecho ativo antes de obter o mapa; preserva seleções se a viagem não mudou.
    setLoading(true);
    try {
      const idaDataStr = formatDataViagemForApi(dataViagem);
      const voltaDataStr = formatDataViagemForApi(dataVolta);

      if (assentosTripRef.current !== opcaoViagem) {
        await consultarViagens(conversationId, {
          origem,
          destino,
          data_viagem: idaDataStr,
          quantidade_passageiros: quantidadePassageiros,
          reset_session: false,
        });
        const idaSeat = await getAssentos(conversationId, Number(opcaoViagem));
        setAssentos({
          disponiveis: idaSeat.assentosdisponiveis || [],
          grid: idaSeat.assentos || [],
          selected: {},
        });
        if (Array.isArray(idaSeat.passengerCategories) && idaSeat.passengerCategories.length) {
          setPassengerCategories(idaSeat.passengerCategories);
        }
        assentosTripRef.current = opcaoViagem;
        if (idaSeat.hasConexoes) {
          const idaCx = await getAssentos(conversationId, Number(opcaoViagem), { perna: 1 });
          setAssentosConexao({
            disponiveis: idaCx.assentosdisponiveis || [],
            grid: idaCx.assentos || [],
            selected: {},
          });
          assentosConexaoTripRef.current = opcaoViagem;
        } else {
          setAssentosConexao({ disponiveis: [], grid: [], selected: {} });
          assentosConexaoTripRef.current = null;
        }
      }

      if (assentosVoltaTripRef.current !== opcaoVolta) {
        await consultarViagens(conversationId, {
          origem: destino,
          destino: origem,
          data_viagem: voltaDataStr,
          quantidade_passageiros: quantidadePassageiros,
          reset_session: false,
        });
        const voltaSeat = await getAssentos(conversationId, Number(opcaoVolta));
        setAssentosVolta({
          disponiveis: voltaSeat.assentosdisponiveis || [],
          grid: voltaSeat.assentos || [],
          selected: {},
        });
        assentosVoltaTripRef.current = opcaoVolta;
        if (voltaSeat.hasConexoes) {
          const voltaCx = await getAssentos(conversationId, Number(opcaoVolta), { perna: 1 });
          setAssentosConexaoVolta({
            disponiveis: voltaCx.assentosdisponiveis || [],
            grid: voltaCx.assentos || [],
            selected: {},
          });
          assentosConexaoVoltaTripRef.current = opcaoVolta;
        } else {
          setAssentosConexaoVolta({ disponiveis: [], grid: [], selected: {} });
          assentosConexaoVoltaTripRef.current = null;
        }
      }

      goForward(2);
    } catch (e) {
      setError(e.message || t("bpeWizard.errors.seats"));
    } finally {
      setLoading(false);
    }
  };

  const loadAssentos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAssentos(conversationId, Number(opcaoViagem));
      assentosTripRef.current = opcaoViagem;
      if (Array.isArray(result.passengerCategories) && result.passengerCategories.length) {
        setPassengerCategories(result.passengerCategories);
      }
      setAssentos((prev) => ({
        ...prev,
        disponiveis: result.assentosdisponiveis || [],
        grid: result.assentos || [],
        selected: {},
      }));
      if (result.hasConexoes) {
        const cx = await getAssentos(conversationId, Number(opcaoViagem), { perna: 1 });
        assentosConexaoTripRef.current = opcaoViagem;
        setAssentosConexao({
          disponiveis: cx.assentosdisponiveis || [],
          grid: cx.assentos || [],
          selected: {},
        });
      } else {
        setAssentosConexao({ disponiveis: [], grid: [], selected: {} });
        assentosConexaoTripRef.current = null;
      }
    } catch (e) {
      setError(e.message || t("bpeWizard.errors.seats"));
    } finally {
      setLoading(false);
    }
  }, [conversationId, opcaoViagem, t]);

  React.useEffect(() => {
    if (activeStep !== 2 || !opcaoViagem) return;
    if (isRoundTrip) return; // ida e volta: os dois mapas são carregados em handleSelecionarViagem
    if (restoringRef.current) return; // a reidratação cuida dos assentos
    // Só (re)carrega quando o mapa ainda não é o desta viagem (preserva seleção ao navegar entre etapas).
    if (assentosTripRef.current === opcaoViagem && assentos.grid.length) return;
    loadAssentos();
  }, [activeStep, opcaoViagem, assentos.grid.length, loadAssentos, isRoundTrip]);

  const handleAssentosContinuar = () => {
    const qtd = quantidadePassageiros;
    const missingIda = [];
    for (let i = 0; i < qtd; i++) {
      if (!assentos.selected[i]) missingIda.push(i + 1);
    }
    if (missingIda.length) {
      setActiveSeatPassenger(missingIda[0] - 1);
      setError(
        t(isRoundTrip ? "bpeWizard.errors.selectSeatIda" : "bpeWizard.errors.selectSeat", {
          list: missingIda.join(", "),
        })
      );
      return;
    }
    if (hasConexaoIda) {
      const missingCx = [];
      for (let i = 0; i < qtd; i++) {
        if (!assentosConexao.selected[i]) missingCx.push(i + 1);
      }
      if (missingCx.length) {
        setActiveSeatPassengerConexao(missingCx[0] - 1);
        setError(t("bpeWizard.errors.selectSeatConexao", { list: missingCx.join(", ") }));
        return;
      }
    }
    if (isRoundTrip) {
      const missingVolta = [];
      for (let i = 0; i < qtd; i++) {
        if (!assentosVolta.selected[i]) missingVolta.push(i + 1);
      }
      if (missingVolta.length) {
        setActiveSeatPassengerVolta(missingVolta[0] - 1);
        setError(t("bpeWizard.errors.selectSeatVolta", { list: missingVolta.join(", ") }));
        return;
      }
      if (hasConexaoVolta) {
        const missingCxV = [];
        for (let i = 0; i < qtd; i++) {
          if (!assentosConexaoVolta.selected[i]) missingCxV.push(i + 1);
        }
        if (missingCxV.length) {
          setActiveSeatPassengerConexaoVolta(missingCxV[0] - 1);
          setError(t("bpeWizard.errors.selectSeatConexaoVolta", { list: missingCxV.join(", ") }));
          return;
        }
      }
    }
    setError(null);
    // Preserva passageiros já preenchidos (ex.: retomada) ajustando ao número de poltronas.
    setPassageiros((prev) =>
      Array.from({ length: qtd }, (_, i) => prev[i] || emptyPassenger())
    );
    setActivePassengerStep(0);
    setPassengerValidationFlash(false);
    goForward(3);
  };

  React.useEffect(() => {
    if (activeStep !== 4 || !open) return;
    getBpeFormasPagamento()
      .then((list) => {
        const methods = Array.isArray(list) ? list : [];
        setPaymentMethods(methods);
        const suggestedId = salesSuggestions?.paymentMethodId;
        const suggested = autoApplyEnabled && suggestedId
          ? methods.find((m) => String(m.id) === String(suggestedId))
          : null;
        const def = suggested || methods.find((m) => m.is_default) || methods[0];
        if (def?.id) {
          // Preserva a forma de pagamento já escolhida (ex.: retomada de rascunho).
          setSelectedPaymentId((prev) => {
            if (prev && methods.some((m) => String(m.id) === String(prev))) return prev;
            if (suggested) suggestionsAppliedRef.current.payment = true;
            return String(def.id);
          });
        }
      })
      .catch(() => setPaymentMethods([]));
  }, [activeStep, open, autoApplyEnabled, salesSuggestions?.paymentMethodId]);

  React.useEffect(() => {
    if (activeStep !== 3 || !open || !autoApplyEnabled || suggestionsAppliedRef.current.passengers) return;
    const p = salesSuggestions?.passenger;
    if (!p?.nome) return;
    setPassageiros((prev) => {
      if (prev[0]?.nome?.trim()) return prev;
      const next = [...prev];
      next[0] = {
        ...next[0],
        nome: p.nome,
        documento: formatCpfDisplay(p.documento) || next[0].documento,
        email: p.email || "",
        celular: p.celular || "",
        clientId: p.clientId || null,
        clientSource: p.clientId ? "inferred" : null,
      };
      return next;
    });
    suggestionsAppliedRef.current.passengers = true;
  }, [activeStep, open, autoApplyEnabled, salesSuggestions]);

  React.useEffect(() => {
    if (activeStep !== 4 || !open || !conversationId) return;
    let cancelled = false;
    (async () => {
      let total = 0;
      let hasBenefit = false;
      const parseTripFare = (trip) =>
        parseFloat(String(trip?.valor || "0").replace(",", ".")) || 0;
      const legs = isRoundTrip
        ? [
            { leg_index: 0, fare: parseTripFare(selectedTrip) },
            { leg_index: 1, fare: parseTripFare(selectedTripVolta) },
          ]
        : [{ leg_index: null, fare: parseTripFare(selectedTrip) }];

      for (const p of passageiros) {
        const cat = p.categoria || "geral";
        if (cat === "geral") {
          total += legs.reduce((sum, leg) => sum + leg.fare, 0);
          continue;
        }
        hasBenefit = true;
        for (const leg of legs) {
          try {
            const q = await cotarGratuidadeBpe(conversationId, {
              ...mapPassengerToApi(p),
              leg_index: leg.leg_index,
            });
            const v = q?.valor ?? q?.valorTarifa ?? q?.valor_beneficio;
            if (v != null) total += Number(v) || 0;
            else total += leg.fare;
          } catch (_) {
            total += leg.fare;
          }
        }
      }
      if (!cancelled) setGratuityFareTotal(hasBenefit ? total : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    open,
    conversationId,
    passageiros,
    selectedTrip?.valor,
    selectedTripVolta?.valor,
    isRoundTrip,
  ]);

  React.useEffect(() => {
    if (!selectedPayment || !isRedeCardPayment(selectedPayment)) return;
    const max = getMaxInstallmentsForPaymentMethod(selectedPayment);
    setRedeCard((prev) => ({
      ...prev,
      installments: clampInstallment(prev.installments, max),
    }));
  }, [selectedPaymentId, selectedPayment]);

  React.useEffect(() => {
    if (activeStep !== 3 || !open || !clientPrefill?.clientId) {
      if (activeStep !== 3) {
        setRequestClient(null);
        setRelatedClients([]);
      }
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const client = await fetchClientRecord(clientPrefill.clientId);
        if (cancelled) return;
        setRequestClient(client);
        const rels = await fetchClientRelationships(clientPrefill.clientId);
        if (cancelled) return;
        setRelatedClients(
          rels
            .map((rel) => mapRelationshipPeer(rel, clientPrefill.clientId))
            .filter(Boolean)
        );
      } catch {
        if (!cancelled) {
          setRequestClient(null);
          setRelatedClients([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeStep, open, clientPrefill?.clientId]);

  const selectedSeatLabels = useCallback(
    () =>
      Array.from({ length: quantidadePassageiros }, (_, i) => assentos.selected[i])
        .filter((s) => s != null && s !== "")
        .map((s) => String(s).padStart(2, "0")),
    [quantidadePassageiros, assentos.selected]
  );

  const selectedSeatLabelsVolta = useCallback(
    () =>
      Array.from({ length: quantidadePassageiros }, (_, i) => assentosVolta.selected[i])
        .filter((s) => s != null && s !== "")
        .map((s) => String(s).padStart(2, "0")),
    [quantidadePassageiros, assentosVolta.selected]
  );

  const runPreReservas = async (legIndex, legTipo, selectedMap = assentos.selected) => {
    const sel = selectedMap;
    let expiresAt = null;
    for (let i = 0; i < passageiros.length; i++) {
      const p = passageiros[i];
      const meta = effectivePassengerCategories.find((c) => c.id === (p.categoria || "geral"));
      const conexaoMap = legTipo === "volta" ? assentosConexaoVolta.selected : assentosConexao.selected;
      const polConexao = conexaoMap?.[i];
      const pr = await preReserva(conversationId, {
        poltrona: sel[i],
        index: i,
        leg_index: legIndex,
        leg_tipo: legTipo,
        ...(polConexao != null && polConexao !== "" ? { assentos_conexoes: [polConexao] } : {}),
        ...mapPassengerToApi(p, {
          declaracao_documentacao_beneficio: meta?.requiresDeclaration ? !!p.declaracaoDoc : false,
        }),
      });
      if (pr.pre_reserva_expires_at) {
        if (!expiresAt || new Date(pr.pre_reserva_expires_at) < new Date(expiresAt)) {
          expiresAt = pr.pre_reserva_expires_at;
        }
      }
    }
    return expiresAt;
  };

  /**
   * Pré-reserva ida e volta numa única passagem: re-conduz o backend perna a perna
   * (cada `consultarViagens`+`obterAssentos` fixa o trecho ativo) e bloqueia as poltronas
   * de cada trecho sob seu `leg_index`. A cobrança é única, ao final.
   */
  const prereserveBothLegs = async () => {
    const idaDataStr = formatDataViagemForApi(dataViagem);
    const voltaDataStr = formatDataViagemForApi(dataVolta);
    // Ida (leg 0): inicia a sessão e fixa o trecho da ida.
    await consultarViagens(conversationId, {
      origem,
      destino,
      data_viagem: idaDataStr,
      quantidade_passageiros: quantidadePassageiros,
      reset_session: true,
    });
    await getAssentos(conversationId, Number(opcaoViagem));
    const idaExp = await runPreReservas(0, "ida", assentos.selected);
    // Volta (leg 1): mantém a ida (reset_session=false) e fixa o trecho da volta.
    await consultarViagens(conversationId, {
      origem: destino,
      destino: origem,
      data_viagem: voltaDataStr,
      quantidade_passageiros: quantidadePassageiros,
      reset_session: false,
    });
    await getAssentos(conversationId, Number(opcaoVolta));
    const voltaExp = await runPreReservas(1, "volta", assentosVolta.selected);
    const exps = [idaExp, voltaExp].filter(Boolean).map((e) => new Date(e).getTime());
    if (exps.length) setPreReservaExpiresAt(new Date(Math.min(...exps)).toISOString());
  };

  const handlePassageirosContinuar = async () => {
    const invalid = passageiros.findIndex(
      (p) =>
        !p.nome?.trim() ||
        !p.documento?.trim() ||
        passengerGratuityError(p, effectivePassengerCategories, t, tripBirthRequired)
    );
    if (invalid >= 0) {
      setActivePassengerStep(invalid);
      setPassengerValidationFlash(true);
      const gratuityErr = passengerGratuityError(
        passageiros[invalid],
        effectivePassengerCategories,
        t,
        tripBirthRequired
      );
      setError(gratuityErr || t("bpeWizard.errors.passengerRequired"));
      return;
    }
    setPassengerValidationFlash(false);
    setError(null);
    setWarning(null);
    setLoading(true);

    try {
      if (syncPassengerRegistry && clientPrefill?.projectId) {
        try {
          const result = await syncPassengersToClientRegistry({
            requestClientId: clientPrefill?.clientId,
            projectId: clientPrefill.projectId,
            passageiros,
          });
          if (result.errors?.length) {
            setWarning(t("bpeWizard.passengerRegistryPartial"));
          }
        } catch (e) {
          setError(e?.message || t("bpeWizard.errors.passengerRegistry"));
          return;
        }
      }

      // A poltrona só é bloqueada (pré-reserva no parceiro) ao gerar a cobrança/link,
      // em handleFinalEmit — não aqui. Assim o assento não fica preso enquanto o
      // atendente apenas escolhe a forma de pagamento.
      goForward(4);
    } catch (e) {
      setError(e?.message || t("bpeWizard.errors.passengerRegistry"));
    } finally {
      setLoading(false);
    }
  };

  const finishWithResults = (results) => {
    setResultado(results);
    setPaymentPending(false);
    setActiveStep(DONE_STEP);
    // Venda concluída: descarta o rascunho para não "retomar" uma venda finalizada.
    clearBpeDraft(conversationId);
    if (onSuccess) onSuccess();
  };

  const handlePaymentPaid = async (statusPayload) => {
    if (emitLockRef.current) return;
    emitLockRef.current = true;
    setLoading(true);
    setError(null);
    try {
      let bpeAuto = statusPayload?.bpeAuto || null;
      let results = mapBpeAutoToResults(bpeAuto);
      if (!results.length) {
        const r = await emitirBpeAposPagamento(conversationId, {
          id_formadepagamento: selectedPaymentId,
        });
        bpeAuto = r?.bpeAuto || bpeAuto;
        results = mapBpeAutoToResults(bpeAuto);
        if (!results.length && r?.dados_bpe) results = [r];
      }
      const failure = extractBpeEmissionFailure(bpeAuto);
      if (failure) {
        // Pagamento confirmado, parte/total da emissão falhou: backend já avisou cliente
        // e registrou nota interna. Mostramos um erro acionável e mantemos o que emitiu.
        setWarning(t("bpeWizard.emissionPartial"));
      }
      if (bpeAuto?.delivery) setDeliveryInfo(bpeAuto.delivery);
      if (!results.length) throw new Error(t("bpeWizard.errors.emit"));
      finishWithResults(results);
    } catch (e) {
      setError(e.message || t("bpeWizard.errors.emit"));
      emitLockRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Abre a pré-visualização do que será enviado ao cliente (Pix/link de pagamento).
  const sendTextToChat = (text) => {
    if (!text || !conversationId) return;
    setPreviewAction({
      key: "payment",
      kind: "text",
      getText: () => text,
      previewTitle: t("bpeWizard.assist.previewTitlePayment"),
      helpText: t("bpeWizard.assist.help.payment"),
    });
  };

  /**
   * Confirmação do preview: NÃO envia sozinho. Joga o conteúdo no compositor do chat
   * (texto via appendText; imagem via imageUrl após upload) e fecha o wizard, para o
   * atendente revisar, editar e enviar manualmente.
   */
  const handleConfirmSend = async ({ kind, text, file, caption } = {}) => {
    if (!conversationId) return;
    setPreviewAction(null);
    try {
      if (kind === "media" && file) {
        const detail = { conversationId: Number(conversationId) };
        try {
          const up = await uploadOne(file, { prefix: "chat" });
          const url = up?.url || up?.fileUrl;
          if (url) detail.imageUrl = url;
        } catch (_) {
          /* sem upload: ao menos manda a legenda */
        }
        if (caption) detail.appendText = caption;
        if (detail.imageUrl || detail.appendText) {
          window.dispatchEvent(new CustomEvent("xpro-chat-compose", { detail }));
        }
      } else if (text) {
        window.dispatchEvent(
          new CustomEvent("xpro-chat-compose", {
            detail: { conversationId: Number(conversationId), appendText: text },
          })
        );
      }
    } finally {
      handleClose();
    }
  };

  const handleFinalEmit = async () => {
    if (!selectedPaymentId) {
      setError(t("bpeWizard.errors.selectPayment"));
      return;
    }
    const invalid = passageiros.findIndex(
      (p) =>
        !p.nome?.trim() ||
        !p.documento?.trim() ||
        passengerGratuityError(p, effectivePassengerCategories, t, tripBirthRequired)
    );
    if (invalid >= 0) {
      const gratuityErr = passengerGratuityError(
        passageiros[invalid],
        effectivePassengerCategories,
        t,
        tripBirthRequired
      );
      setError(gratuityErr || t("bpeWizard.errors.passengerRequired"));
      return;
    }
    if (paymentPending) {
      emitLockRef.current = false;
      await handlePaymentPaid({});
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // Bloqueia as poltronas AGORA (pré-reserva no parceiro), apenas ao gerar a
      // cobrança/link ou emitir. Em ida e volta, bloqueia as duas pernas (pagamento único).
      try {
        if (isRoundTrip) {
          await prereserveBothLegs();
        } else {
          const exp = await runPreReservas(null, null, assentos.selected);
          if (exp) setPreReservaExpiresAt(exp);
        }
      } catch (e) {
        // Poltrona ocupada nesse meio-tempo: avisa e volta para a escolha de poltronas,
        // forçando o recarregamento do mapa para refletir a disponibilidade atual.
        setError(e?.message || t("bpeWizard.errors.preReserve"));
        assentosTripRef.current = null;
        assentosVoltaTripRef.current = null;
        assentosConexaoTripRef.current = null;
        assentosConexaoVoltaTripRef.current = null;
        setActiveStep(2);
        return;
      }

      if (isImmediateBpePayment(selectedPayment)) {
        const results = await emitAllLegsImmediate();
        finishWithResults(results);
        return;
      }

      if (isRedeCardPayment(selectedPayment)) {
        const cardErr = validateRedeCard(redeCard, t);
        if (cardErr) {
          setError(cardErr);
          return;
        }
        const charge = await chargeBpePayment(conversationId, {
          id_formadepagamento: selectedPaymentId,
          card: cardToApiPayload(redeCard),
        });
        const st = String(charge?.status || "").toUpperCase();
        if (charge.kind === "card" && st === "PAID") {
          const results = mapBpeAutoToResults(charge.bpeAuto);
          if (results.length) {
            finishWithResults(results);
            return;
          }
        }
        if (charge.kind === "card" && st === "PENDING") {
          setPaymentCharge(charge);
          setPaymentPending(true);
          emitLockRef.current = false;
          return;
        }
        setError(charge?.reason || charge?.message || t("bpeWizard.cardRefused"));
        return;
      }

      if (needsOnlineBpeCharge(selectedPayment)) {
        const charge = await chargeBpePayment(conversationId, {
          id_formadepagamento: selectedPaymentId,
        });
        setPaymentCharge(charge);
        setPaymentPending(true);
        emitLockRef.current = false;
        return;
      }

      // Apenas dinheiro emite sem confirmação; qualquer outra forma sem cobrança
      // reconhecida não pode emitir o BPe sem a devida confirmação do pagamento.
      setError(t("bpeWizard.errors.paymentConfirmationRequired"));
    } catch (e) {
      setError(e.message || t("bpeWizard.errors.emit"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Emissão imediata (sem cobrança online), cobrindo todos os trechos pré-reservados.
   * Em ida e volta, emite leg 0 (ida) e leg 1 (volta); em somente-ida, leg null.
   */
  const emitAllLegsImmediate = async () => {
    const legIdxs = isRoundTrip ? [0, 1] : [null];
    const results = [];
    for (const legIdx of legIdxs) {
      for (let i = 0; i < passageiros.length; i++) {
        const r = await confirmarReserva(conversationId, {
          leg_index: legIdx,
          passageiro_index: i,
          id_formadepagamento: selectedPaymentId,
          ...mapPassengerToApi(passageiros[i]),
        });
        results.push({ ...r, legIndex: legIdx });
      }
    }
    return results;
  };

  const handlePassageirosChange = (index, field, value) => {
    setPassengerValidationFlash(false);
    setPassageiros((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (field !== "clientId" && field !== "clientSource") {
        next[index].clientSource = next[index].clientId ? "registry_edited" : null;
      }
      return next;
    });
  };

  const applyClientToPassenger = async (index, clientId) => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const client = Number(clientId) === Number(requestClient?.id)
        ? requestClient
        : await fetchClientRecord(clientId);
      const fields = clientRecordToPassenger(client);
      if (!fields) return;
      setPassageiros((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...fields };
        return next;
      });
    } catch (e) {
      setError(e?.message || t("bpeWizard.errors.passengerRegistryLoad"));
    } finally {
      setLoading(false);
    }
  };

  const clearPassengerFields = (index) => {
    setPassageiros((prev) => {
      const next = [...prev];
      next[index] = emptyPassenger();
      return next;
    });
    setPassengerValidationFlash(false);
  };

  const splitResultsByLeg = useCallback((results) => {
    const ida = [];
    const volta = [];
    (results || []).forEach((r) => {
      const li = r.legIndex != null ? Number(r.legIndex) : r.legTipo === "volta" ? 1 : 0;
      if (li === 1) volta.push(r);
      else ida.push(r);
    });
    return { ida, volta };
  }, []);

  const buildSuccessSummary = useCallback(() => {
    const legLines = (results) =>
      (results || []).map((r) => {
        const loc = r.dados_bpe?.Localizador || r.reservationId || "—";
        const num = r.dados_bpe?.NumeroBpe || "—";
        return `${t("bpeWizard.locator")}: ${loc} · ${t("bpeWizard.bpeNumber")}: ${num}`;
      });

    if (isRoundTrip) {
      const { ida, volta } = splitResultsByLeg(resultado);
      const parts = [];
      const idaHeader = routeSummary ? `${t("bpeWizard.legIda")} — ${routeSummary}` : t("bpeWizard.legIda");
      parts.push(`${idaHeader}\n${legLines(ida).join("\n")}`);
      const voltaHeader = voltaRouteSummary ? `${t("bpeWizard.legVolta")} — ${voltaRouteSummary}` : t("bpeWizard.legVolta");
      parts.push(`${voltaHeader}\n${legLines(volta).join("\n")}`);
      return parts.join("\n\n");
    }

    if (!resultado?.length) return "";
    const header = routeSummary ? `${routeSummary}\n` : "";
    return `${header}${legLines(resultado).join("\n")}`;
  }, [resultado, routeSummary, voltaRouteSummary, isRoundTrip, splitResultsByLeg, t]);

  // ---- Atalhos de venda assistida (conteúdo enviado ao cliente por etapa) ----
  const buildSchedulesText = useCallback(() => {
    const top = (viagens || []).slice(0, 6);
    if (!top.length) return "";
    const lines = top.map((v, i) => {
      const classe = normalizeTripClasse(v.classe);
      return `${i + 1}) ${v.inicio}–${v.fim}${classe ? ` · ${classe}` : ""} · ${formatBRL(v.valor)}`;
    });
    return `${routeSummary ? `${routeSummary}\n` : ""}${t("bpeWizard.assist.schedulesHeader")}\n${lines.join("\n")}`;
  }, [viagens, routeSummary, t]);

  const buildSeatMapFile = useCallback(
    () =>
      renderSeatMapImage(assentos.grid, {
        disponiveis: assentos.disponiveis,
        selected: assentos.selected,
        title: routeSummary,
      }),
    [assentos.grid, assentos.disponiveis, assentos.selected, routeSummary]
  );

  const buildConfirmationText = useCallback(() => {
    const lines = [t("bpeWizard.assist.confirmationHeader")];
    const seatsLine = (labels) =>
      labels?.length ? `   ${t("bpeWizard.assist.seatsLabel")}: ${labels.join(", ")}` : null;
    if (isRoundTrip) {
      lines.push(`${t("bpeWizard.legIda")}: ${routeSummary || ""}`);
      if (selectedTrip) lines.push(`   ${selectedTrip.inicio}–${selectedTrip.fim} · ${formatBRL(selectedTrip.valor)}`);
      const s1 = seatsLine(selectedSeatLabels());
      if (s1) lines.push(s1);
      lines.push(`${t("bpeWizard.legVolta")}: ${voltaRouteSummary || ""}`);
      if (selectedTripVolta) lines.push(`   ${selectedTripVolta.inicio}–${selectedTripVolta.fim} · ${formatBRL(selectedTripVolta.valor)}`);
      const s2 = seatsLine(selectedSeatLabelsVolta());
      if (s2) lines.push(s2);
    } else {
      if (routeSummary) lines.push(routeSummary);
      if (selectedTrip) lines.push(`${selectedTrip.inicio}–${selectedTrip.fim} · ${formatBRL(selectedTrip.valor)}`);
      const s = seatsLine(selectedSeatLabels());
      if (s) lines.push(s);
    }
    const names = passageiros.filter((p) => p.nome?.trim()).map((p, i) => `${t("bpeWizard.passengerN", { n: i + 1 })}: ${p.nome}`);
    if (names.length) lines.push("", ...names);
    return lines.join("\n");
  }, [isRoundTrip, routeSummary, voltaRouteSummary, selectedTrip, selectedTripVolta, selectedSeatLabels, selectedSeatLabelsVolta, passageiros, t]);

  const buildResendText = useCallback(() => {
    const base = buildSuccessSummary();
    const links = Array.isArray(deliveryInfo?.pdfs) ? deliveryInfo.pdfs.filter(Boolean) : [];
    if (!links.length) return base;
    return `${base}\n\n${links.join("\n")}`;
  }, [buildSuccessSummary, deliveryInfo]);

  const assistedActionsForStep = useMemo(() => {
    if (!assisted) return [];
    if (activeStep === 1 && viagens.length) {
      return [
        {
          key: "schedules",
          kind: "text",
          label: t("bpeWizard.assist.sendSchedules"),
          getText: buildSchedulesText,
          helpText: t("bpeWizard.assist.help.schedules"),
          previewTitle: t("bpeWizard.assist.previewTitleSchedules"),
        },
      ];
    }
    if (activeStep === 2 && assentos.grid?.length) {
      return [
        {
          key: "seatmap",
          kind: "media",
          label: t("bpeWizard.assist.sendSeatMap"),
          getFile: buildSeatMapFile,
          caption: routeSummary || t("bpeWizard.assist.seatMapCaption"),
          helpText: t("bpeWizard.assist.help.seatmap"),
          previewTitle: t("bpeWizard.assist.previewTitleSeatMap"),
        },
      ];
    }
    if (activeStep === 4 && !paymentPending) {
      return [
        {
          key: "confirmation",
          kind: "text",
          label: t("bpeWizard.assist.sendConfirmation"),
          getText: buildConfirmationText,
          helpText: t("bpeWizard.assist.help.confirmation"),
          previewTitle: t("bpeWizard.assist.previewTitleConfirmation"),
        },
      ];
    }
    if (activeStep === DONE_STEP) {
      return [
        {
          key: "resend",
          kind: "text",
          label: t("bpeWizard.assist.resendTicket"),
          getText: buildResendText,
          helpText: t("bpeWizard.assist.help.resend"),
          previewTitle: t("bpeWizard.assist.previewTitleResend"),
        },
      ];
    }
    return [];
  }, [
    assisted,
    activeStep,
    viagens.length,
    assentos.grid,
    paymentPending,
    routeSummary,
    buildSchedulesText,
    buildSeatMapFile,
    buildConfirmationText,
    buildResendText,
    t,
  ]);

  const handleCopySuccessToChat = () => {
    const text = buildSuccessSummary();
    if (!text || !conversationId) return;
    setPreviewAction({
      key: "success",
      kind: "text",
      getText: () => text,
      previewTitle: t("bpeWizard.assist.previewTitleResend"),
      helpText: t("bpeWizard.assist.help.resend"),
    });
  };

  const handleClose = () => {
    // Fechar mantém o rascunho salvo (retomada). Faz um flush do estado atual
    // (o debounce do autosave poderia perder a última alteração) e evita autosave vazio.
    if (
      draftReadyRef.current &&
      activeStep !== DONE_STEP &&
      (origem || destino || activeStep > 0)
    ) {
      saveBpeDraft(conversationId, buildDraft()).catch(() => {});
    }
    draftReadyRef.current = false;
    restoringRef.current = false;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    setDraftPhase("idle");
    setResumeAvailable(false);
    setResumeDegraded(false);
    setDownstreamStale(false);
    setFurthestStep(0);
    assentosTripRef.current = null;
    setActiveStep(0);
    setError(null);
    setWarning(null);
    setTripNotice(null);
    setViagens([]);
    setOpcaoViagem("");
    setAssentos({ disponiveis: [], grid: [], selected: {} });
    setActiveSeatPassenger(0);
    setViagensVolta([]);
    setOpcaoVolta("");
    setAssentosVolta({ disponiveis: [], grid: [], selected: {} });
    setActiveSeatPassengerVolta(0);
    assentosVoltaTripRef.current = null;
    setAssentosConexao({ disponiveis: [], grid: [], selected: {} });
    setAssentosConexaoVolta({ disponiveis: [], grid: [], selected: {} });
    assentosConexaoTripRef.current = null;
    assentosConexaoVoltaTripRef.current = null;
    setActivePassengerStep(0);
    setPassengerValidationFlash(false);
    setPassageiros([emptyPassenger()]);
    setRequestClient(null);
    setRelatedClients([]);
    setSyncPassengerRegistry(true);
    setPaymentMethods([]);
    setSelectedPaymentId("");
    setPaymentCharge(null);
    setPaymentPending(false);
    setRedeCard({ name: "", number: "", expiry: "", cvv: "", installments: "1" });
    emitLockRef.current = false;
    setPreReservaExpiresAt(null);
    setResultado(null);
    setDeliveryInfo(null);
    setSalesSuggestions(null);
    setSuggestionsLoading(false);
    setAutoApplyEnabled(false);
    setTripType("one_way");
    setDataVolta("");
    routeTouchedRef.current = false;
    suggestionsAppliedRef.current = {
      route: false,
      routeDestino: false,
      trip: false,
      passengers: false,
      payment: false,
    };
    onClose();
  };

  /** Descarta o rascunho e recomeça a venda do zero (a pedido do atendente). */
  const handleDiscardDraft = async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    restoringRef.current = true;
    try {
      await clearBpeDraft(conversationId);
    } catch (_) {
      /* best-effort */
    }
    applyFreshDefaults();
    setResultado(null);
    setDeliveryInfo(null);
    setPaymentPending(false);
    setPaymentCharge(null);
    emitLockRef.current = false;
    setError(null);
    setWarning(null);
    setTripNotice(null);
    setResumeAvailable(false);
    setResumeDegraded(false);
    setDownstreamStale(false);
    setDraftPhase("fresh");
    restoringRef.current = false;
    draftReadyRef.current = true;
  };

  const toggleSeat = (passengerIndex, seatNum) => {
    setAssentos((prev) => {
      const current = prev.selected[passengerIndex];
      const nextSelected = { ...prev.selected };
      if (String(current) === String(seatNum)) {
        delete nextSelected[passengerIndex];
      } else {
        nextSelected[passengerIndex] = seatNum;
      }
      if (quantidadePassageiros > 1) {
        let nextActive = passengerIndex;
        if (nextSelected[passengerIndex] != null) {
          const missing = [];
          for (let i = 0; i < quantidadePassageiros; i++) {
            if (!nextSelected[i]) missing.push(i);
          }
          if (missing.length) nextActive = missing[0];
        }
        setActiveSeatPassenger(nextActive);
      }
      return { ...prev, selected: nextSelected };
    });
  };

  const makeToggleSeat = (setSeats, setActive) => (passengerIndex, seatNum) => {
    setSeats((prev) => {
      const current = prev.selected[passengerIndex];
      const nextSelected = { ...prev.selected };
      if (String(current) === String(seatNum)) {
        delete nextSelected[passengerIndex];
      } else {
        nextSelected[passengerIndex] = seatNum;
      }
      if (quantidadePassageiros > 1) {
        let nextActive = passengerIndex;
        if (nextSelected[passengerIndex] != null) {
          const missing = [];
          for (let i = 0; i < quantidadePassageiros; i++) {
            if (!nextSelected[i]) missing.push(i);
          }
          if (missing.length) nextActive = missing[0];
        }
        setActive(nextActive);
      }
      return { ...prev, selected: nextSelected };
    });
  };

  const toggleSeatConexao = makeToggleSeat(setAssentosConexao, setActiveSeatPassengerConexao);
  const toggleSeatConexaoVolta = makeToggleSeat(setAssentosConexaoVolta, setActiveSeatPassengerConexaoVolta);

  const toggleSeatVolta = (passengerIndex, seatNum) => {
    setAssentosVolta((prev) => {
      const current = prev.selected[passengerIndex];
      const nextSelected = { ...prev.selected };
      if (String(current) === String(seatNum)) {
        delete nextSelected[passengerIndex];
      } else {
        nextSelected[passengerIndex] = seatNum;
      }
      if (quantidadePassageiros > 1) {
        let nextActive = passengerIndex;
        if (nextSelected[passengerIndex] != null) {
          const missing = [];
          for (let i = 0; i < quantidadePassageiros; i++) {
            if (!nextSelected[i]) missing.push(i);
          }
          if (missing.length) nextActive = missing[0];
        }
        setActiveSeatPassengerVolta(nextActive);
      }
      return { ...prev, selected: nextSelected };
    });
  };

  const passengersCompleteCount = useMemo(
    () =>
      passageiros.filter((p) => isPassengerFormComplete(p, effectivePassengerCategories, tripBirthRequired))
        .length,
    [passageiros, effectivePassengerCategories, tripBirthRequired]
  );

  if (!conversationId) return null;

  const progressPct = ((activeStep + 1) / stepLabels.length) * 100;
  // Passos com lista/mapa próprios (Horário/Poltronas) gerenciam o próprio scroll;
  // os demais rolam junto com os avisos para não espremer o conteúdo.
  const isFillStep = activeStep === 1 || activeStep === 2;
  const hasClientPrefill = Boolean(clientPrefill?.clientId);
  const canSyncPassengerRegistry = Boolean(clientPrefill?.projectId);
  const returnDateInvalid =
    tripType === "round_trip" &&
    legPhase === "ida" &&
    (() => {
      const ida = formatDateForInput(dataViagem);
      const volta = formatDateForInput(dataVolta);
      return !volta || volta < ida;
    })();

  // Guia sequencial: quando a etapa atual está completa, o "próximo clique" é o botão de ação.
  const ctaReady =
    !loading &&
    !paymentPending &&
    draftPhase !== "loading" &&
    activeStep < DONE_STEP &&
    isStepComplete(activeStep) &&
    !(activeStep === 0 && returnDateInvalid);
  const ctaGuideSx = ctaReady ? bpeGuideButtonSx : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={BPE_WIZARD_MAX_WIDTH}
      fullWidth
      PaperProps={{ sx: bpeWizardPaperSx }}
    >
      <DialogTitle sx={bpeWizardTitleSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box minWidth={0}>
            <Typography variant="h6" component="span" fontWeight={700} lineHeight={1.2}>
              {t("bpeWizard.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("bpeWizard.subtitle")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            {tripType === "round_trip" && activeStep < DONE_STEP && (
              <Chip
                size="small"
                label={t("bpeWizard.tripType.roundTrip")}
                color="secondary"
                sx={{ fontWeight: 700 }}
              />
            )}
            <Chip
              size="small"
              label={stepLabels[activeStep]}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={bpeWizardContentSx}>
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t("bpeWizard.stepOf", {
              current: activeStep + 1,
              total: stepLabels.length,
              label: stepLabels[activeStep],
            })}
          </Typography>
          <LinearProgress variant="determinate" value={progressPct} sx={{ mt: 0.5, borderRadius: 1, height: 4 }} />
        </Box>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          nonLinear
          sx={{ ...bpeWizardStepperSx, display: { xs: "none", md: "flex" } }}
        >
          {stepLabels.map((label, i) => {
            const isCurrent = i === activeStep;
            const reachable = canNavigateTo(i);
            return (
              <Step key={label} completed={i < activeStep && isStepComplete(i)}>
                <StepButton
                  color="inherit"
                  onClick={() => goToStep(i)}
                  disabled={!reachable && !isCurrent}
                  sx={{ cursor: reachable ? "pointer" : "default" }}
                >
                  {label}
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        {/* Região rolável: nos passos sem lista própria, avisos e conteúdo rolam juntos. */}
        <Box sx={isFillStep ? bpeWizardStepBodyFillSx : bpeWizardStepBodySx}>
        {activeStep > 0 && activeStep < DONE_STEP && (
          <BPeTripContextBar
            routeSummary={routeSummary}
            trip={activeStep >= 2 ? selectedTrip : null}
            selectedSeats={
              activeStep >= 3
                ? Array.from({ length: quantidadePassageiros }, (_, i) => assentos.selected[i]).filter(
                    (s) => s != null && s !== ""
                  )
                : undefined
            }
            formatPrice={formatBRL}
            normalizeClasse={normalizeTripClasse}
            t={t}
          />
        )}

        {resumeAvailable && activeStep < DONE_STEP && (
          <Alert
            severity="info"
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            onClose={() => setResumeAvailable(false)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleDiscardDraft}
                disabled={loading}
                sx={{ whiteSpace: "nowrap", textTransform: "none" }}
              >
                {t("bpeWizard.resume.startOver")}
              </Button>
            }
            sx={{
              mb: 1,
              flexShrink: 0,
              py: 0,
              alignItems: "center",
              "& .MuiAlert-message": { py: 0.5, overflow: "hidden" },
            }}
          >
            <Tooltip
              title={
                resumeCrossChannel
                  ? t("bpeWizard.resume.resumedCrossChannelHint")
                  : t("bpeWizard.resume.resumedHint")
              }
            >
              <Typography variant="caption" fontWeight={700} component="span" noWrap>
                {resumeCrossChannel ? t("bpeWizard.resume.resumedCrossChannel") : t("bpeWizard.resume.resumed")}
              </Typography>
            </Tooltip>
          </Alert>
        )}

        {resumeDegraded && activeStep < DONE_STEP && (
          <Alert
            severity="warning"
            onClose={() => setResumeDegraded(false)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setResumeReloadKey((k) => k + 1)}
                disabled={loading || draftPhase === "loading"}
                sx={{ whiteSpace: "nowrap", textTransform: "none" }}
              >
                {t("bpeWizard.resume.reload")}
              </Button>
            }
            sx={{ mb: 1, flexShrink: 0, py: 0.25 }}
          >
            {t("bpeWizard.resume.degraded")}
          </Alert>
        )}

        {downstreamStale && activeStep < DONE_STEP && (
          <Alert severity="warning" onClose={() => setDownstreamStale(false)} sx={{ mb: 1, flexShrink: 0, py: 0.25 }}>
            {t("bpeWizard.resume.downstreamStale")}
          </Alert>
        )}

        {tripNotice && activeStep === 0 && (
          <Alert severity="warning" onClose={() => setTripNotice(null)} sx={{ mb: 1, flexShrink: 0, py: 0.25 }}>
            {tripNotice}
          </Alert>
        )}

        {legPhase === "ida" &&
          (activeStep === 0 || activeStep === 1 || activeStep === 4) &&
          // No pagamento, não sugere forma quando há só uma opção (escolha óbvia).
          !(activeStep === 4 && paymentMethods.length <= 1) && (
            <BPeSuggestionBar
              step={activeStep}
              suggestions={salesSuggestions}
              loading={suggestionsLoading}
              onApply={handleApplySuggestion}
              applyDisabled={loading}
              t={t}
            />
          )}

        {warning && (
          <Alert severity="warning" onClose={() => setWarning(null)} sx={{ mb: 1, flexShrink: 0, py: 0.25 }}>
            {warning}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, flexShrink: 0, py: 0.25 }}>
            {error}
          </Alert>
        )}

        {assistedActionsForStep.length > 0 && (
          <BPeAssistedActions
            t={t}
            assisted={assisted}
            actions={assistedActionsForStep}
            onPreview={setPreviewAction}
          />
        )}

        <Box sx={isFillStep ? bpeWizardStepBodyFillSx : bpeWizardStepInnerSx}>
          {draftPhase === "loading" && (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="caption" color="text.secondary">
                {t("bpeWizard.resume.loading")}
              </Typography>
            </Stack>
          )}
          {draftPhase !== "loading" && activeStep === 0 && (
            <BPeRouteStep
              t={t}
              origem={origem}
              destino={destino}
              dataViagem={dataViagem}
              quantidadePassageiros={quantidadePassageiros}
              cidadesOrigem={cidadesOrigem}
              cidadesDestino={cidadesDestino}
              loadingCidadesOrigem={loadingCidadesOrigem}
              loadingCidadesDestino={loadingCidadesDestino}
              origemSel={origemSel}
              destinoSel={destinoSel}
              onOrigemChange={(postback) => {
                routeTouchedRef.current = true;
                invalidateDownstream(0);
                setOrigem(postback);
                setDestino("");
                setError(null);
              }}
              onDestinoChange={(postback) => {
                routeTouchedRef.current = true;
                invalidateDownstream(0);
                setDestino(postback);
                setError(null);
              }}
              onSwapRoute={handleSwapRoute}
              onDataViagemChange={(value) => {
                routeTouchedRef.current = true;
                invalidateDownstream(0);
                setDataViagem(value);
                setTripNotice(null);
              }}
              onPassengerCountChange={(value) => {
                routeTouchedRef.current = true;
                invalidateDownstream(0);
                setQuantidadePassageiros(value);
              }}
              tripType={tripType}
              onTripTypeChange={(value) => {
                setTripType(value);
                if (value === "round_trip" && !dataVolta) {
                  setDataVolta(formatDateForInput(dataViagem) || todayLocalISO());
                }
              }}
              dataVolta={dataVolta}
              onDataVoltaChange={(value) => setDataVolta(value)}
              legPhase={legPhase}
            />
          )}

          {activeStep === 1 &&
            (isRoundTrip ? (
              <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <LegSectionHeader
                    label={t("bpeWizard.legIda")}
                    color="primary"
                    route={routeSummary}
                    detail={selectedTrip ? `${selectedTrip.inicio}–${selectedTrip.fim}` : null}
                  />
                  <BPeTripOptionList
                    viagens={viagens}
                    selectedOption={opcaoViagem}
                    onSelect={(value) => {
                      if (value !== opcaoViagem) invalidateDownstream(1);
                      setOpcaoViagem(value);
                    }}
                    passengerCount={quantidadePassageiros}
                    t={t}
                  />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <LegSectionHeader
                    label={t("bpeWizard.legVolta")}
                    color="secondary"
                    route={voltaRouteSummary}
                    detail={selectedTripVolta ? `${selectedTripVolta.inicio}–${selectedTripVolta.fim}` : null}
                  />
                  <BPeTripOptionList
                    viagens={viagensVolta}
                    selectedOption={opcaoVolta}
                    onSelect={(value) => {
                      if (value !== opcaoVolta) invalidateDownstream(1);
                      setOpcaoVolta(value);
                    }}
                    passengerCount={quantidadePassageiros}
                    t={t}
                  />
                </Box>
              </Stack>
            ) : (
              <BPeTripOptionList
                viagens={viagens}
                selectedOption={opcaoViagem}
                onSelect={(value) => {
                  // Trocar a viagem invalida os assentos já escolhidos (mapa é por viagem).
                  if (value !== opcaoViagem) invalidateDownstream(1);
                  setOpcaoViagem(value);
                }}
                passengerCount={quantidadePassageiros}
                t={t}
              />
            ))}

          {activeStep === 2 &&
            (isRoundTrip ? (
              <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <LegSectionHeader
                    label={t("bpeWizard.legIda")}
                    color="primary"
                    route={routeSummary}
                    detail={selectedTrip ? `${selectedTrip.inicio}–${selectedTrip.fim}` : null}
                  />
                  <BPeSeatStep
                    loading={loading}
                    quantidadePassageiros={quantidadePassageiros}
                    assentos={assentos}
                    activeSeatPassenger={activeSeatPassenger}
                    onActivePassengerChange={setActiveSeatPassenger}
                    onSelectSeat={toggleSeat}
                    t={t}
                  />
                  {hasConexaoIda ? (
                    <Box sx={{ mt: 1 }}>
                      <LegSectionHeader
                        label={t("bpeWizard.connectionLeg")}
                        color="info"
                        route={tripItineraryLabel(selectedTrip)}
                        detail={t("bpeWizard.connectionLegN", { n: 2 })}
                      />
                      <BPeSeatStep
                        loading={loading}
                        quantidadePassageiros={quantidadePassageiros}
                        assentos={assentosConexao}
                        activeSeatPassenger={activeSeatPassengerConexao}
                        onActivePassengerChange={setActiveSeatPassengerConexao}
                        onSelectSeat={toggleSeatConexao}
                        t={t}
                      />
                    </Box>
                  ) : null}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <LegSectionHeader
                    label={t("bpeWizard.legVolta")}
                    color="secondary"
                    route={voltaRouteSummary}
                    detail={selectedTripVolta ? `${selectedTripVolta.inicio}–${selectedTripVolta.fim}` : null}
                  />
                  <BPeSeatStep
                    loading={loading}
                    quantidadePassageiros={quantidadePassageiros}
                    assentos={assentosVolta}
                    activeSeatPassenger={activeSeatPassengerVolta}
                    onActivePassengerChange={setActiveSeatPassengerVolta}
                    onSelectSeat={toggleSeatVolta}
                    t={t}
                  />
                  {hasConexaoVolta ? (
                    <Box sx={{ mt: 1 }}>
                      <LegSectionHeader
                        label={t("bpeWizard.connectionLeg")}
                        color="info"
                        route={tripItineraryLabel(selectedTripVolta)}
                        detail={t("bpeWizard.connectionLegN", { n: 2 })}
                      />
                      <BPeSeatStep
                        loading={loading}
                        quantidadePassageiros={quantidadePassageiros}
                        assentos={assentosConexaoVolta}
                        activeSeatPassenger={activeSeatPassengerConexaoVolta}
                        onActivePassengerChange={setActiveSeatPassengerConexaoVolta}
                        onSelectSeat={toggleSeatConexaoVolta}
                        t={t}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
                <BPeSeatStep
                  loading={loading}
                  quantidadePassageiros={quantidadePassageiros}
                  assentos={assentos}
                  activeSeatPassenger={activeSeatPassenger}
                  onActivePassengerChange={setActiveSeatPassenger}
                  onSelectSeat={toggleSeat}
                  t={t}
                />
                {hasConexaoIda ? (
                  <Box>
                    <LegSectionHeader
                      label={t("bpeWizard.connectionLeg")}
                      color="info"
                      route={tripItineraryLabel(selectedTrip)}
                      detail={t("bpeWizard.connectionLegN", { n: 2 })}
                    />
                    <BPeSeatStep
                      loading={loading}
                      quantidadePassageiros={quantidadePassageiros}
                      assentos={assentosConexao}
                      activeSeatPassenger={activeSeatPassengerConexao}
                      onActivePassengerChange={setActiveSeatPassengerConexao}
                      onSelectSeat={toggleSeatConexao}
                      t={t}
                    />
                  </Box>
                ) : null}
              </Stack>
            ))}

          {activeStep === 3 && (
            <BPePassengerStep
              passageiros={passageiros}
              assentosSelected={assentos.selected}
              activePassengerIndex={activePassengerStep}
              passengerCategories={effectivePassengerCategories}
              requireBirthDate={tripBirthRequired}
              onActivePassengerChange={(i) => {
                setActivePassengerStep(i);
                setPassengerValidationFlash(false);
              }}
              onChange={handlePassageirosChange}
              hasClientPrefill={hasClientPrefill}
              canSyncRegistry={canSyncPassengerRegistry}
              showValidation={passengerValidationFlash}
              requestClient={requestClient}
              relatedClients={relatedClients}
              onApplyRequestClient={() => applyClientToPassenger(0, clientPrefill?.clientId)}
              onApplyRelatedClient={(clientId) => applyClientToPassenger(activePassengerStep, clientId)}
              onClearPassenger={clearPassengerFields}
              syncRegistry={syncPassengerRegistry}
              onSyncRegistryChange={setSyncPassengerRegistry}
              t={t}
            />
          )}

          {activeStep === 4 && (
            <BPePaymentStep
              t={t}
              paymentMethods={paymentMethods}
              selectedPaymentId={selectedPaymentId}
              onSelectPayment={setSelectedPaymentId}
              selectedPayment={selectedPayment}
              formatPrice={formatBRL}
              paymentPending={paymentPending}
              paymentCharge={paymentCharge}
              preReservaExpiresAt={preReservaExpiresAt}
              loading={loading}
              conversationId={conversationId}
              onPaid={handlePaymentPaid}
              onSendToChat={sendTextToChat}
              redeCard={redeCard}
              onRedeCardChange={setRedeCard}
              passengerCount={quantidadePassageiros}
              roundTrip={isRoundTrip}
              selectedTrip={isRoundTrip ? selectedTripVolta : selectedTrip}
              outboundTrip={isRoundTrip ? selectedTrip : null}
              outboundSummary={isRoundTrip ? routeSummary : ""}
              returnSummary={isRoundTrip ? voltaRouteSummary : ""}
              gratuityFareTotal={gratuityFareTotal}
            />
          )}

          {activeStep === DONE_STEP && resultado && (
            <BPeDoneStep
              t={t}
              resultado={isRoundTrip ? splitResultsByLeg(resultado).volta : resultado}
              passageiros={passageiros}
              onCopyToChat={handleCopySuccessToChat}
              outboundResults={isRoundTrip ? splitResultsByLeg(resultado).ida : null}
              outboundSummary={isRoundTrip ? routeSummary : ""}
              returnSummary={isRoundTrip ? voltaRouteSummary : ""}
              deliveryInfo={deliveryInfo}
            />
          )}
        </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={bpeWizardActionsSx}>
        {activeStep === 1 && !isRoundTrip && opcaoViagem && selectedTrip && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: "1 1 100%", order: -1, lineHeight: 1.3, mb: -0.5 }}
          >
            {t("bpeWizard.selectedTripLine", {
              dep: selectedTrip.inicio,
              arr: selectedTrip.fim,
              classe: normalizeTripClasse(selectedTrip.classe) || "—",
              price: formatBRL(selectedTrip.valor),
            })}
            {hasConexaoIda
              ? ` · ${tripItineraryLabel(selectedTrip) || t("bpeWizard.withConnection")}`
              : ""}
          </Typography>
        )}
        {activeStep === 2 && !isRoundTrip && (() => {
          const seatLabels = [];
          for (let i = 0; i < quantidadePassageiros; i++) {
            if (assentos.selected[i]) {
              seatLabels.push(String(assentos.selected[i]).padStart(2, "0"));
            }
          }
          if (!seatLabels.length) return null;
          const allSeats = seatLabels.length === quantidadePassageiros;
          return (
            <Typography
              variant="caption"
              color={allSeats ? "success.main" : "text.secondary"}
              fontWeight={700}
              sx={{ flex: "1 1 100%", order: -1, lineHeight: 1.3, mb: -0.5 }}
            >
              {t("bpeWizard.seatsFooterSummary", { seats: seatLabels.join(", ") })}
            </Typography>
          );
        })()}
        {activeStep > 0 && activeStep < DONE_STEP && (
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={loading} sx={{ mr: "auto" }}>
            {t("bpeWizard.back")}
          </Button>
        )}
        {activeStep < DONE_STEP && (
          <Button onClick={handleClose} disabled={loading}>
            {t("bpeWizard.cancel")}
          </Button>
        )}
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleConsultar}
            disabled={loading || !origem || !destino || returnDateInvalid}
            size="large"
            sx={{ fontWeight: 700, ...(ctaGuideSx || {}) }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : t("bpeWizard.searchTrips")}
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleSelecionarViagem}
            disabled={loading || !opcaoViagem || (isRoundTrip && !opcaoVolta)}
            size="large"
            sx={{ minWidth: 140, fontWeight: 700, ...(ctaGuideSx || {}) }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : t("bpeWizard.continue")}
          </Button>
        )}
        {activeStep === 2 && (() => {
          const countLeg = (sel) => {
            let n = 0;
            for (let i = 0; i < quantidadePassageiros; i++) {
              if (sel[i]) n += 1;
            }
            return n;
          };
          const idaDone = countLeg(assentos.selected);
          const voltaDone = isRoundTrip ? countLeg(assentosVolta.selected) : 0;
          const totalNeeded = isRoundTrip ? quantidadePassageiros * 2 : quantidadePassageiros;
          const totalDone = idaDone + voltaDone;
          const allSeats = totalDone === totalNeeded;
          return (
            <Button
              variant="contained"
              onClick={handleAssentosContinuar}
              disabled={loading || !allSeats}
              size="large"
              sx={{ minWidth: 160, fontWeight: 700, ...(allSeats ? ctaGuideSx || {} : {}) }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : allSeats ? (
                t("bpeWizard.continue")
              ) : (
                t("bpeWizard.continueSeats", {
                  done: totalDone,
                  total: totalNeeded,
                })
              )}
            </Button>
          );
        })()}
        {activeStep === 3 && (() => {
          const allPassengers = passengersCompleteCount === passageiros.length;
          return (
            <Button
              variant="contained"
              onClick={handlePassageirosContinuar}
              disabled={loading || !allPassengers}
              size="large"
              sx={{ minWidth: 160, fontWeight: 700, ...(allPassengers ? ctaGuideSx || {} : {}) }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : allPassengers ? (
                t("bpeWizard.continue")
              ) : (
                t("bpeWizard.continuePassengers", {
                  done: passengersCompleteCount,
                  total: passageiros.length,
                })
              )}
            </Button>
          );
        })()}
        {activeStep === 4 && (
          <Button
            variant="contained"
            onClick={handleFinalEmit}
            disabled={
              loading ||
              paymentPending ||
              (!paymentPending && (!selectedPaymentId || paymentMethods.length === 0))
            }
            size="large"
            sx={{ minWidth: 160, fontWeight: 700, ...(ctaGuideSx || {}) }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : paymentPending ? (
              t("bpeWizard.waitingPayment")
            ) : isRedeCardPayment(selectedPayment) ? (
              t("bpeWizard.chargeCard")
            ) : needsOnlineBpeCharge(selectedPayment) ? (
              t("bpeWizard.generateCharge")
            ) : (
              t("bpeWizard.confirmEmit")
            )}
          </Button>
        )}
        {activeStep === DONE_STEP && (
          <Button variant="contained" onClick={handleClose} size="large" sx={{ fontWeight: 700 }}>
            {t("bpeWizard.close")}
          </Button>
        )}
      </DialogActions>

      <BPeSendPreviewDialog
        open={Boolean(previewAction)}
        action={previewAction}
        onClose={() => setPreviewAction(null)}
        onConfirm={handleConfirmSend}
        t={t}
      />
    </Dialog>
  );
};

export default BPeWizard;
